import React, { useState, useEffect } from 'react';
import '../App.css';
import Calendar from './Calendar';
import { useOpointAuth, useOpointUser } from '@opoint/authjs-react';
import {  Box, IconButton, Tab, Tabs, CircularProgress } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { getAssignments, getTeams, getTasks, createOrUpdateTask, deleteTask } from '../api';
import { ConfigurationTeam, Task, Assignment } from '../types';
import { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import TaskSelector from './TaskSelector';
import TaskModal from './TaskModal';

const newTask: Task = {
    name: 'New task',
    color: '#000000',
    active: false,
    description: '',
};

const checkEditableDate = (date: Dayjs): boolean => {
    return date >= dayjs().startOf('day').subtract(1, 'day');
}

const findInTeam = (team: ConfigurationTeam, idUser: number): boolean => {
    return team.members.some((member) => member.id_user === idUser);
}

const getModulo = (teams: ConfigurationTeam[], idUser: number): number => {
    teams.forEach((team, index) => {
        if (findInTeam(team, idUser)) {
            return team.modulo;
        }
    });
    return 0;
}

function DailyTasks() {
    const auth = useOpointAuth();
    const idUser = useOpointUser()?.user_id;

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string|boolean>(false);
    const [teams, setTeams] = useState<ConfigurationTeam[] >([]);
    const [previousTask, setPreviousTask] = useState<Task>();
    const [selectedTask, setSelectedTask] = useState<Task>(newTask);
    const [date, setDate] = useState<Dayjs>(dayjs());
    const [tasks, setTasks] = useState<Task[]>([]);
    const [taskModalOpen, setTaskModalOpen] = useState(false);

    // calendar should only be editable if
    // - the user is a source config leader
    // - the date is yesterday or later, and the user is a member of the selected team
    const [isLeader, setIsLeader] = useState(false);
    const [isTeamMember, setIsTeamMember] = useState(false);
    const [isEditableDate, setIsEditableDate] = useState(false);
    const [editable, setEditable] = useState(false);

    useEffect(() => {
        setIsEditableDate(checkEditableDate(date));
    }, [date]);

    const [modulo, setModulo] = useState(0);
    const [team, setTeam] = useState(teams[modulo]);

    useEffect(() => {
        if (team !== undefined && idUser !== undefined) {
            setIsTeamMember(findInTeam(team, idUser!));
        }
    }, [team, idUser]);

    useEffect(() => {
        setTeam(teams[modulo]);
    }, [modulo, teams]);

    useEffect(() => {

        setIsLeader(
            idUser !== undefined
            && (
                idUser === 1  // root
                || idUser === 2338  // Steinar Lund
                || idUser === 4378  // Rune Kleveland
                || idUser === 18011  // Madhavi Gangu
                || idUser === 35937  // Mikael Gundhus
                || idUser === 152187  // Simon Nordås
            )
        );

        getTasks(auth)
            .then((tasks) => {
                setTasks(tasks);
                setLoading(false);
            })
            .catch((err) => setError(true));

    }, [auth, idUser]);

    useEffect(() => {
        getTeams(auth, date)
            .then((teams) => {
                setTeams(teams);
                setModulo(getModulo(teams, idUser!));
            })
            .catch((err) => setError(true));
    }, [auth, idUser, date]);

    useEffect(() => {
        setEditable(
            isLeader
            || (isEditableDate && isTeamMember)
        );
    }, [isLeader, isEditableDate, isTeamMember]);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setModulo(newValue);
    }

    const handleDateChange = (date: Dayjs | null) => {
        if (date === null) {
            date = dayjs();
        }
        setDate(date);
    }

    const [assignments, setAssignments] = useState<Assignment[]>([]);

    useEffect(() => {
        if (team !== undefined) {
            getAssignments(auth, team.modulo, date)
                .then((assignments) => {
                    setAssignments(assignments);
                })
                .catch((err) => setError(true));
        }
    }, [auth, date, team]);

    const handleCreateTask = () => {
        setTaskModalOpen(true);
        setSelectedTask(newTask);
    }

    const handleCloseTaskModal = () => {
        setTaskModalOpen(false);
        if (tasks !== undefined && tasks.length > 0) {
            if (previousTask !== undefined) {
                setSelectedTask(previousTask);
            }
        }
    }

    const handleDeleteTaskModal = (task: Task) => {
        deleteTask(auth, task.id_task!)
            .then(() => {
                setTasks(tasks.filter((t) => t.id_task !== task.id_task));
                handleCloseTaskModal();
            });
    }

    const handleSaveTask = (task: Task) => {
        const created = (
            task.id_task === undefined
            ? true
            : false
        );

        createOrUpdateTask(auth, task)
            .then((result) => {
                // the function returns undefined if it's an update and the
                // id_task if it's a create, that's a bit silly
                if (result !== undefined) {
                    task.id_task = result;
                }
                handleCloseTaskModal();
                if (created) {
                    setTasks([...tasks, task]);
                } else {
                    setTasks(tasks.map((t) => t.id_task === task.id_task ? task : t));
                }
            });
    }

    useEffect(() => {
        if (previousTask !== undefined && selectedTask !== undefined) {
            if (selectedTask.id_task === undefined) return;
            if (previousTask.id_task !== selectedTask.id_task) {
                setPreviousTask(selectedTask);
            }
        } else {
            setPreviousTask(selectedTask);
        }
    }, [selectedTask, previousTask]);

    if (loading) {
        return (
            <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                top="50%"
            >
                <CircularProgress />
            </Box>
        );
    } // Error handling here later
    else if (error) {
        return (
            <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                marginTop="100px"
            >
                <h1>Error</h1>
            </Box>
        );
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className="toolbar">
                <div className="toolbar-left">
                    <div className="tabs">
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Tabs value={modulo} onChange={handleTabChange} aria-label="simple tabs example">
                                {[0, 1, 2, 3, 4].map((id) => (
                                    <Tab key={id} label={id} />
                                ))}
                            </Tabs>
                        </Box>
                    </div>
                </div>
                <div className="toolbar-right">
                    <div className="date-picker">
                        <DatePicker
                            label="Date"
                            onChange={handleDateChange}
                        />
                    </div>
                </div>
            </div>
            <div className="calendar-wrapper">
                <div>
                    <Calendar
                        modulo={modulo}
                        assignments={assignments}
                        setAssignments={setAssignments}
                        team={team}
                        editable={editable}
                        selectedTask={selectedTask}
                        tasks={tasks}
                        date={date}
                        useIndianTime={true}
                    />
                </div>
            </div>
            <div className="task-selector">
                <TaskSelector
                    selectedTask={selectedTask}
                    setSelectedTask={setSelectedTask}
                    tasks={tasks}
                    setTaskModalOpen={setTaskModalOpen}
                    assignments={assignments}
                />
                {isLeader
                    ? <>
                        <TaskModal
                            open={isLeader && taskModalOpen}
                            task={selectedTask}
                            onClose={handleCloseTaskModal}
                            onSave={handleSaveTask}
                            onDelete={handleDeleteTaskModal}
                            setTask={setSelectedTask}
                        />
                        <IconButton aria-label="add" onClick={handleCreateTask}>
                            <AddIcon />
                        </IconButton>
                    </>
                    : null
                }
            </div>
        </LocalizationProvider>
    );
}

export default DailyTasks;